/* eslint-disable react/jsx-props-no-spreading */
// import * as ReactDOMClient from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { usePeopleListingQuery, type PeopleListingQuery } from 'cms/cms';
import { resolvePeopleListing } from 'cms/resolvers/peopleListingResolver';
import PeopleListingProvider from './PeopleListingProvider';
import PeopleListingContext, { type PeopleListingContextProps, type PeopleListingProps } from './PeopleListingContext';
import { getQueryURL, getFilterQuery, getWhereValue, type WhereItem, getPageQueryURL } from '3-components/ListingApp/ListingFilters/ListingFilterHelpers';
import { DEFAULT_FETCH_PARAMS, OPTMIZELY_GRAPHQL_BASE_URL } from 'cms/constants';
import ListingHeader from '3-components/ListingApp/ListingHeader/ListingHeader';
import ListingContent from '3-components/ListingApp/ListingContent/ListingContent';
import { ListingError, ListingLoader } from '3-components/ListingApp/ListingUI/ListingUI';
import CardPerson from '3-components/Cards/CardPerson/CardPerson';
import { type ActiveFilter } from '3-components/ListingApp/ListingFilters/ListingFiltersTypes';


const PeopleListing: FC = () => {
    const {
        locale,
        activeFilters,
        setActiveFilters,
        listing: {
            filters,
            filterByGuid,
            filterByAncestors,
            itemsPerPage = 10,
        },
        page,
        setPage,
        label,
        cms,
        serverData,
        serverPageUrl,
    } = useContext(PeopleListingContext) as PeopleListingContextProps;

    const parsedServerData = serverData && typeof serverData === 'string' ? JSON.parse(serverData) : serverData;
    const [isClient, setIsClient] = useState(false);

    const { search, ...restParams } = getFilterQuery(activeFilters, filters);
    const scrollToEl = useRef<HTMLDivElement>(null);

    const updateFilters = (value: ActiveFilter[]): void => {
        setPage(0);
        setActiveFilters(value);
    };


    const whereItems: WhereItem[] = [];
    if (search) {
        whereItems.push({
            type: 'search',
            field: '_fulltext',
            value: search,
        });
    }

    if (filterByGuid) {
        whereItems.push({
            type: 'guid',
            field: 'GuidValue',
            value: filterByGuid,
        });
    }

    if (filterByAncestors) {
        whereItems.push({
            type: 'ancestors',
            field: 'Ancestors',
            value: filterByAncestors,
        });
    }

    const where = getWhereValue(whereItems);

    // We control when this is called through the enabled option
    const queryResult = usePeopleListingQuery({
        endpoint: `${cms.apiUrl ? cms.apiUrl : OPTMIZELY_GRAPHQL_BASE_URL}${cms.apiKey}`,
        fetchParams: DEFAULT_FETCH_PARAMS,
    }, {
        locale,
        where,
        ...restParams,
        skip: page * itemsPerPage,
        limit: filterByGuid ? 100 : itemsPerPage,
    }, {

        queryKey: ['people-cards'],
        keepPreviousData: true,
        staleTime: 1000 * 60 * 5,
        initialData: !isClient && parsedServerData?.data ? parsedServerData.data : undefined,
        select: useCallback((result: PeopleListingQuery) => {
            return resolvePeopleListing(result, { ctaLabel: label.cardCtaLabel, filterByGuid });
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []), // https://tkdodo.eu/blog/react-query-data-transformations
    });

    const data = queryResult?.data;
    const { error = false, isLoading = false, isError = false, isFetching = false } = queryResult || {};

    useEffect(() => {
        setIsClient(true);
    }, []);

    useEffect(() => {
        window.history.replaceState({}, '', getQueryURL(activeFilters, filters));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters]);

    useEffect(() => {
        if (scrollToEl?.current) {
            scrollToEl.current.scrollIntoView({ behavior: 'smooth' });
        }

        window.history.replaceState({}, '', getPageQueryURL(page));
    }, [page]);

    if (isLoading) {
        return (
            <ListingLoader loadingMessage={label.loadingMessage} />
        );
    }

    if (isError || !data) {
        console.error(error);

        return (
            <ListingError errorMessage={label.errorMessage} />
        );
    }

    return (
        <div className="listing-app" ref={scrollToEl}>
            <div className="container">
                {(label.title || label.subtitle) && (
                    <div className="section-intro">
                        <div className="section-intro-row">
                            <div className="section-intro-col">
                                <div className="section-intro-content-row">
                                    <div className="section-intro-content-col">
                                        <h2 className="section-intro-title">{label.title}</h2>
                                    </div>
                                    {label.subtitle && (
                                        <div className="section-intro-content-col">
                                            <div className="section-intro-blurb rich-text-spacing">
                                                {label.subtitle}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {!!filters && !!filters.length && (
                    <ListingHeader
                        filters={filters}
                        activeFilters={activeFilters}
                        setActiveFilters={updateFilters}
                        title={label.filterHeading || ''}
                        clearFilterLabel={label.clearFilterButton}
                    />
                )}
                <ListingContent
                    isFetching={isFetching}
                    isNoItems={!data.items.length}
                    pagination={{
                        itemsPerPage: filterByGuid ? 100 : itemsPerPage,
                        page,
                        setPage,
                        totalItems: data.total,
                        serverPageUrl: serverPageUrl ?? '',
                    }}
                    loadingLabel={label.loadingMessage}
                    noItemsLabel={label.noItemMessage}
                    cols="4"
                >
                    {data.items.map(props => {
                        const key = `${props.title}-${props.link?.href}`;
                        return (
                            <CardPerson key={key} {...props} />
                        );
                    })}
                </ListingContent>
            </div>
        </div>
    );
};

const PeopleListingWithProvider: FC<PeopleListingProps> = props => {
    // eslint-disable-next-line react/hook-use-state
    const [queryClient] = useState(() => {
        return new QueryClient();
    });

    useEffect(() => {
        return () => {
            queryClient.clear(); // Clean up on unmount
        };
    }, [queryClient]);

    return (
        <QueryClientProvider client={queryClient}>
            <PeopleListingProvider {...props}>
                <PeopleListing />
            </PeopleListingProvider>
        </QueryClientProvider>
    );
};

export default PeopleListingWithProvider;
